import { useRef } from 'react'
import { Box, Button, Chip, TextField, Typography } from '@mui/material'
import { Form, FieldArray, useFormikContext, FieldArrayRenderProps } from 'formik'

import { IHashTagsSettingsForm } from 'src/types'
import { useTranslation } from 'react-i18next'

interface IProps {
  handleClose: () => void
  helperText?: string
  placeholder?: string
}

export const SettingListItemsForm = ({ handleClose, helperText, placeholder }: IProps) => {
  const [t] = useTranslation()
  const { values, handleChange, setFieldValue, isSubmitting } =
    useFormikContext<IHashTagsSettingsForm>()

  const initialHashTagsRef = useRef(values.hashTags)

  const hasHashTagsChanged = () =>
    JSON.stringify(values.hashTags) !== JSON.stringify(initialHashTagsRef.current)

  const handleAdd = (arrayHelpers: FieldArrayRenderProps) => {
    const newTag = values.newTag?.trim()
    if (newTag && !values.hashTags.includes(newTag)) {
      arrayHelpers.push(newTag)
      setFieldValue('newTag', '')
    }
  }

  return (
    <Form style={{ display: 'flex', flex: 1 }}>
      <FieldArray
        name="hashTags"
        render={(arrayHelpers) => (
          <Box
            sx={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              gap: '20px',
              justifyContent: 'space-between',
              mt: '30px',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Box sx={{ display: 'flex', gap: '10px' }}>
                <TextField
                  name="newTag"
                  value={values.newTag || ''}
                  onChange={handleChange}
                  sx={{ flex: 1 }}
                  inputProps={{ style: { padding: '6px 16px' } }}
                  placeholder={placeholder || undefined}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    handleAdd(arrayHelpers)
                  }}
                  disabled={!values.newTag?.trim() || isSubmitting}
                >
                  {t('button-add-new-text.v')}
                </Button>
              </Box>

              {helperText && (
                <Typography sx={{ maxWidth: '360px' }} variant="caption" color="textSecondary">
                  {helperText}
                </Typography>
              )}

              <Box sx={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {values.hashTags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => arrayHelpers.remove(index)}
                    sx={{ backgroundColor: 'action.hover', fontWeight: 500, userSelect: 'text' }}
                  />
                ))}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: '25px',
                width: '100%',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Button variant="outlined" size="small" onClick={handleClose} disabled={isSubmitting}>
                {t('cancel-button-text.v')}
              </Button>
              <Button
                type="submit"
                variant="contained"
                size="small"
                disabled={isSubmitting || !hasHashTagsChanged()}
              >
                {t('save-button-text.v')}
              </Button>
            </Box>
          </Box>
        )}
      />
    </Form>
  )
}
